import { useEffect, useMemo, useState } from "react"
import { opsAdminApi } from "@/lib/opsAdminApi"

type RangePreset = "7d" | "30d" | "90d"

type ProviderCostResponse = Awaited<ReturnType<typeof opsAdminApi.getLlmProviderCosts>>

function toIsoDay(ms: number) {
  return new Date(ms).toISOString().slice(0, 10)
}

function getPresetDays(preset: RangePreset) {
  if (preset === "7d") return 7
  if (preset === "90d") return 90
  return 30
}

function buildWindow(preset: RangePreset) {
  const now = Date.now()
  const days = getPresetDays(preset)
  const fromMs = now - ((days - 1) * 24 * 60 * 60 * 1000)
  return {
    from: toIsoDay(fromMs),
    to: toIsoDay(now),
    days,
  }
}

function formatUsd(value: number) {
  return `$${Number(value || 0).toFixed(4)}`
}

function formatDateLabel(value: string) {
  if (!value) return "—"
  const date = new Date(`${value}T00:00:00Z`)
  if (!Number.isFinite(date.getTime())) return value
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function providerColor(provider: string) {
  const normalized = String(provider || "").toLowerCase()
  if (normalized === "openai") return "#10a37f"
  if (normalized === "anthropic") return "#d97706"
  return "var(--sf-accent, #3b82f6)"
}

export function AiCcExpensesCard() {
  const [preset, setPreset] = useState<RangePreset>("30d")
  const [provider, setProvider] = useState("")
  const [breakdownType, setBreakdownType] = useState("")
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState("")
  const [data, setData] = useState<ProviderCostResponse | null>(null)

  const window = useMemo(() => buildWindow(preset), [preset])

  const providerRows = useMemo(() => {
    const rows = Array.isArray(data?.providers) ? data.providers : []
    return rows.sort((a, b) => Number(b.total_usd || 0) - Number(a.total_usd || 0))
  }, [data?.providers])

  const breakdownTypeRows = useMemo(() => {
    const rows = Array.isArray(data?.breakdown_types) ? data.breakdown_types : []
    return rows.sort((a, b) => Number(b.total_usd || 0) - Number(a.total_usd || 0))
  }, [data?.breakdown_types])

  const topBreakdownKeys = useMemo(() => {
    const rows = Array.isArray(data?.top_breakdown_keys) ? data.top_breakdown_keys : []
    return rows.slice(0, 24)
  }, [data?.top_breakdown_keys])

  const dailySeries = useMemo(() => {
    const rows = Array.isArray(data?.daily) ? data.daily : []
    const grouped = new Map<string, { day: string; amount: number; providers: Set<string> }>()
    for (const row of rows) {
      const day = String(row.day || "").trim()
      if (!day) continue
      const amount = Number(row.amount_usd || 0)
      if (!grouped.has(day)) {
        grouped.set(day, { day, amount: 0, providers: new Set() })
      }
      const current = grouped.get(day)
      if (!current) continue
      current.amount += amount
      const p = String(row.provider || "").trim()
      if (p) current.providers.add(p)
    }
    return Array.from(grouped.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .slice(-40)
      .map((row) => ({
        day: row.day,
        amount: Number(row.amount.toFixed(6)),
        providers: Array.from(row.providers).join(", "),
      }))
  }, [data?.daily])

  const maxDaily = useMemo(() => {
    if (!dailySeries.length) return 0
    return Math.max(...dailySeries.map((row) => row.amount))
  }, [dailySeries])

  const latestSync = useMemo(() => {
    const rows = Array.isArray(data?.sync_logs) ? data.sync_logs : []
    return rows.length > 0 ? rows[0] : null
  }, [data?.sync_logs])

  const breakdownTypeOptions = useMemo(() => {
    const rows = Array.isArray(data?.breakdown_types) ? data.breakdown_types : []
    const set = new Set<string>()
    for (const row of rows) {
      const value = String(row.type || "").trim()
      if (value) set.add(value)
    }
    if (breakdownType) set.add(breakdownType)
    return Array.from(set).sort()
  }, [data?.breakdown_types, breakdownType])

  useEffect(() => {
    loadData().catch(() => {})
  }, [window.from, window.to, provider, breakdownType])

  async function loadData() {
    setLoading(true)
    setError("")
    const res = await opsAdminApi.getLlmProviderCosts({
      from: window.from,
      to: window.to,
      provider: provider || undefined,
      breakdownType: breakdownType || undefined,
      topKeysLimit: 120,
      breakdownLimit: 2400,
      syncLogLimit: 30,
    })
    if (!res.ok) {
      setData(null)
      const apiError = (res as Record<string, unknown>).error
      setError(typeof apiError === "string" ? apiError : "Failed to load provider expense report.")
      setLoading(false)
      return
    }
    setData(res)
    setLoading(false)
  }

  async function handleSync() {
    setSyncing(true)
    setError("")
    const res = await opsAdminApi.triggerLlmSync(window.days)
    if (!res.ok) {
      setError(res.error || "Provider cost sync failed.")
      setSyncing(false)
      return
    }
    await loadData()
    setSyncing(false)
  }

  const totals = data?.totals || {
    total_usd: 0,
    days_with_data: 0,
    daily_avg_usd: 0,
    peak_day: "",
    peak_day_cost_usd: 0,
  }

  const openaiShare = providerRows.find((row) => row.provider === "openai")?.share_pct || 0
  const anthropicShare = providerRows.find((row) => row.provider === "anthropic")?.share_pct || 0

  return (
    <div className="section-card ai-cc-expenses-card">
      <div className="ai-cc-expenses-card__header">
        <div>
          <div className="section-card__title" style={{ marginBottom: 0 }}>Expenses (Provider Billing APIs)</div>
          <p className="ai-cc-expenses-card__subtitle">
            OpenAI `/organization/costs` + Anthropic `/organizations/cost_report`
          </p>
        </div>
        <div className="ai-cc-expenses-card__sync">
          <span className="ai-cc-expenses-card__sync-text">
            {latestSync
              ? `Last sync: ${new Date(latestSync.finished_at || latestSync.started_at).toLocaleString()} (${latestSync.status})`
              : "No sync yet"}
          </span>
          <button
            type="button"
            className="sf-button sf-button--sm sf-button--secondary"
            onClick={() => handleSync().catch(() => {})}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync Billing"}
          </button>
        </div>
      </div>

      <div className="ai-cc-expenses-card__filters">
        <div className="ops-chip-row">
          <button type="button" className={`ops-chip ${preset === "7d" ? "ops-chip--active" : ""}`} onClick={() => setPreset("7d")}>7d</button>
          <button type="button" className={`ops-chip ${preset === "30d" ? "ops-chip--active" : ""}`} onClick={() => setPreset("30d")}>30d</button>
          <button type="button" className={`ops-chip ${preset === "90d" ? "ops-chip--active" : ""}`} onClick={() => setPreset("90d")}>90d</button>
        </div>
        <select className="ai-cc-event-select" value={provider} onChange={(event) => setProvider(event.target.value)}>
          <option value="">All providers</option>
          <option value="openai">OpenAI</option>
          <option value="anthropic">Anthropic</option>
        </select>
        <select className="ai-cc-event-select" value={breakdownType} onChange={(event) => setBreakdownType(event.target.value)}>
          <option value="">All breakdown types</option>
          {breakdownTypeOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>

      {loading && (
        <div className="ai-cc-expenses-card__loading">
          <div className="sf-spinner" style={{ margin: "0 auto" }} />
        </div>
      )}

      {!loading && (
        <>
          {error && <div className="ai-cc-error">{error}</div>}

          <div className="ai-cc-expenses-card__kpis">
            <ExpenseKpi label="Total Spend" value={formatUsd(totals.total_usd)} hint={`${window.from} → ${window.to}`} />
            <ExpenseKpi label="Daily Avg" value={formatUsd(totals.daily_avg_usd)} hint={`${totals.days_with_data} active days`} />
            <ExpenseKpi label="Peak Day" value={totals.peak_day ? formatDateLabel(totals.peak_day) : "—"} hint={formatUsd(totals.peak_day_cost_usd)} />
            <ExpenseKpi label="OpenAI Share" value={`${openaiShare.toFixed(1)}%`} hint={formatUsd(providerRows.find((row) => row.provider === "openai")?.total_usd || 0)} />
            <ExpenseKpi label="Anthropic Share" value={`${anthropicShare.toFixed(1)}%`} hint={formatUsd(providerRows.find((row) => row.provider === "anthropic")?.total_usd || 0)} />
          </div>

          <div className="ai-cc-expenses-card__grid">
            <div className="data-table-wrapper">
              <div className="ops-llm-table-title">Daily Spend</div>
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Providers</th>
                    <th>Cost</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {dailySeries.slice().reverse().map((row) => {
                    const pct = maxDaily > 0 ? Math.max(3, Math.round((row.amount / maxDaily) * 100)) : 0
                    return (
                      <tr key={row.day}>
                        <td className="ops-llm-mono">{row.day}</td>
                        <td>{row.providers || "—"}</td>
                        <td className="ops-llm-cost">{formatUsd(row.amount)}</td>
                        <td>
                          <div className="ai-cc-expenses-card__spark">
                            <div className="ai-cc-expenses-card__spark-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {dailySeries.length === 0 && <div className="ops-llm-empty">No daily provider billing data in this range.</div>}
            </div>

            <div className="data-table-wrapper">
              <div className="ops-llm-table-title">Breakdown Types</div>
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>Days</th>
                    <th>Rows</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownTypeRows.map((row, index) => (
                    <tr key={`${row.provider}-${row.type}-${index}`}>
                      <td>
                        <span className="ai-cc-expenses-card__provider-dot" style={{ background: providerColor(row.provider) }} />
                        {row.provider}
                      </td>
                      <td>{row.type}</td>
                      <td>{row.days_with_data}</td>
                      <td>{row.row_count}</td>
                      <td className="ops-llm-cost">{formatUsd(row.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {breakdownTypeRows.length === 0 && <div className="ops-llm-empty">No breakdown type data in this range.</div>}
            </div>
          </div>

          <div className="data-table-wrapper">
            <div className="ops-llm-table-title">Top Cost Drivers</div>
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>Key</th>
                  <th>Days</th>
                  <th>Rows</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {topBreakdownKeys.map((row, index) => (
                  <tr key={`${row.provider}-${row.type}-${row.key}-${index}`}>
                    <td>
                      <span className="ai-cc-expenses-card__provider-dot" style={{ background: providerColor(row.provider) }} />
                      {row.provider}
                    </td>
                    <td>{row.type}</td>
                    <td className="ops-llm-mono">{row.key}</td>
                    <td>{row.days_with_data}</td>
                    <td>{row.row_count}</td>
                    <td className="ops-llm-cost">{formatUsd(row.total_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topBreakdownKeys.length === 0 && <div className="ops-llm-empty">No cost drivers in this range.</div>}
          </div>

          <div className="data-table-wrapper">
            <div className="ops-llm-table-title">Provider Sync History</div>
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Days</th>
                  <th>Trigger</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {(data?.sync_logs || []).map((log) => (
                  <tr key={log.id}>
                    <td className="ops-llm-mono">{new Date(log.started_at).toLocaleString()}</td>
                    <td>{log.provider}</td>
                    <td>
                      <span className={`status-pill status-pill--${log.status === "ok" ? "healthy" : log.status === "running" ? "neutral" : "error"}`}>
                        <span className="status-pill__dot" />
                        {log.status}
                      </span>
                    </td>
                    <td>{log.days_synced}</td>
                    <td>{log.trigger_type}</td>
                    <td>{log.error_message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data?.sync_logs || data.sync_logs.length === 0) && <div className="ops-llm-empty">No sync history yet.</div>}
          </div>
        </>
      )}
    </div>
  )
}

function ExpenseKpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="metric-card metric-card--compact">
      <div className="metric-card__label">{label}</div>
      <div className="metric-card__value">{value}</div>
      <div className="metric-card__sub">{hint}</div>
    </div>
  )
}
